import React, { useState, useEffect } from 'react';
import {
  Building2,
  Users,
  DollarSign,
  FileText,
  Clock,
  TrendingUp,
  AlertTriangle,
  CheckCircle,
  Settings,
  BarChart3,
  UserPlus,
  Calendar,
  Target,
  Mail
} from 'lucide-react';
import { useAuthStore, api } from '../auth/authStore';
import { FirmAdminLayout } from './FirmAdminLayout';
import toast from 'react-hot-toast';

interface DashboardOverview {
  firm: {
    name: string;
    totalUsers: number;
    activeUsers: number;
    totalClients: number;
    activeClients: number;
    emailTemplatesCount: number;
  };
  financial: {
    monthlyRevenue: number;
    outstandingInvoices: number;
    overdueInvoices: number;
    totalInvoicesThisMonth: number;
  };
  productivity: {
    activeTasks: number;
    completedTasksThisMonth: number;
    totalHoursThisMonth: number;
    billableHoursThisMonth: number;
    utilizationRate: number;
  };
  recentActivity: Array<{
    type: string;
    description: string;
    timestamp: string;
  }>;
}

const StatCard: React.FC<{
  title: string;
  value: string | number;
  change?: string;
  icon: React.ElementType;
  color: string;
  trend?: 'up' | 'down' | 'neutral';
}> = ({ title, value, change, icon: Icon, color, trend }) => (
  <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
    <div className="flex items-center justify-between">
      <div>
        <p className="text-sm font-medium text-gray-600 mb-1">{title}</p>
        <p className="text-2xl font-bold text-gray-900">{value}</p>
        {change && (
          <p className={`text-sm flex items-center mt-1 ${
            trend === 'up' ? 'text-green-600' : 
            trend === 'down' ? 'text-red-600' : 
            'text-gray-600'
          }`}>
            {trend === 'up' && <TrendingUp className="w-4 h-4 mr-1" />}
            {change}
          </p>
        )}
      </div>
      <div className={`w-12 h-12 ${color} rounded-xl flex items-center justify-center`}>
        <Icon className="w-6 h-6 text-white" />
      </div>
    </div>
  </div>
);

const QuickActionCard: React.FC<{
  title: string;
  description: string;
  icon: React.ElementType;
  color: string;
  onClick: () => void;
}> = ({ title, description, icon: Icon, color, onClick }) => (
  <div 
    className="bg-white rounded-xl shadow-sm p-6 border border-gray-200 cursor-pointer hover:shadow-md transition-shadow"
    onClick={onClick}
  >
    <div className="flex items-start space-x-4">
      <div className={`w-10 h-10 ${color} rounded-lg flex items-center justify-center`}>
        <Icon className="w-5 h-5 text-white" />
      </div>
      <div>
        <h3 className="font-medium text-gray-900 mb-1">{title}</h3>
        <p className="text-sm text-gray-600">{description}</p>
      </div>
    </div>
  </div>
);

export const FirmAdminDashboard: React.FC = () => {
  const { user, tenant } = useAuthStore();
  const [overview, setOverview] = useState<DashboardOverview | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    fetchDashboardOverview();
  }, []);

  const fetchDashboardOverview = async () => {
    try {
      setLoading(true);
      const response = await api.get('/firm-admin/dashboard');
      
      if (response.data.success) {
        setOverview(response.data.data);
      } else {
        throw new Error('Failed to fetch dashboard overview');
      }
    } catch (error) {
      console.error('Error fetching dashboard overview:', error);
      setError('Failed to load dashboard data');
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  const handleQuickAction = (action: string) => {
    switch (action) {
      case 'add_user':
        // Navigate to user management
        toast.info('Navigating to user management...');
        break;
      case 'create_invoice':
        // Navigate to invoice creation
        toast.info('Navigating to invoice creation...');
        break;
      case 'view_reports':
        // Navigate to reports
        toast.info('Navigating to reports...');
        break;
      case 'manage_clients':
        // Navigate to client management
        toast.info('Navigating to client management...');
        break;
      default:
        break;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-500 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error || !overview) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertTriangle className="w-12 h-12 text-red-500 mx-auto mb-4" />
          <p className="text-gray-600 mb-4">{error || 'Failed to load dashboard'}</p>
          <button
            onClick={fetchDashboardOverview}
            className="px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <FirmAdminLayout>
      <div className="p-6">
        {/* Welcome Section */}
        <div className="mb-8">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">
            Welcome back, {user?.firstName}!
          </h2>
          <p className="text-gray-600">
            Here's an overview of your firm's performance and key metrics.
          </p>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            title="Total Users"
            value={overview.firm.totalUsers}
            change={`${overview.firm.activeUsers} active`}
            icon={Users}
            color="bg-blue-500"
            trend="neutral"
          />
          <StatCard
            title="Active Clients"
            value={overview.firm.activeClients}
            change={`of ${overview.firm.totalClients} total`}
            icon={Building2}
            color="bg-green-500"
            trend="neutral"
          />
          <StatCard
            title="Monthly Revenue"
            value={`$${overview.financial.monthlyRevenue.toLocaleString()}`}
            change={`${overview.financial.totalInvoicesThisMonth} invoices`}
            icon={DollarSign}
            color="bg-purple-500"
            trend="up"
          />
          <StatCard
            title="Utilization Rate"
            value={`${overview.productivity.utilizationRate}%`}
            change={`${overview.productivity.billableHoursThisMonth}h billable`}
            icon={Target}
            color="bg-orange-500"
            trend="up"
          />
        </div>

        {/* Quick Actions */}
        <div className="mb-8">
          <h3 className="text-lg font-medium text-gray-900 mb-4">Quick Actions</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <QuickActionCard
              title="Add New User"
              description="Invite team members to join your firm"
              icon={UserPlus}
              color="bg-blue-500"
              onClick={() => handleQuickAction('add_user')}
            />
            <QuickActionCard
              title="Create Invoice"
              description="Generate and send invoices to clients"
              icon={FileText}
              color="bg-green-500"
              onClick={() => handleQuickAction('create_invoice')}
            />
            <QuickActionCard
              title="View Reports"
              description="Access detailed analytics and insights"
              icon={BarChart3}
              color="bg-purple-500"
              onClick={() => handleQuickAction('view_reports')}
            />
            <QuickActionCard
              title="Manage Clients"
              description="Add and organize client information"
              icon={Building2}
              color="bg-orange-500"
              onClick={() => handleQuickAction('manage_clients')}
            />
          </div>
        </div>

        {/* Overview Cards */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Financial Overview */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Financial Overview</h3>
              <DollarSign className="w-5 h-5 text-gray-400" />
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Outstanding Invoices</span>
                <span className="font-medium text-orange-600">
                  ${overview.financial.outstandingInvoices.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Overdue Invoices</span>
                <span className="font-medium text-red-600">
                  ${overview.financial.overdueInvoices.toLocaleString()}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">This Month's Revenue</span>
                <span className="font-medium text-green-600">
                  ${overview.financial.monthlyRevenue.toLocaleString()}
                </span>
              </div>
            </div>
          </div>

          {/* Productivity Overview */}
          <div className="bg-white rounded-xl shadow-sm p-6 border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-medium text-gray-900">Productivity Overview</h3>
              <Clock className="w-5 h-5 text-gray-400" />
            </div>
            <div className="space-y-4">
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Active Tasks</span>
                <span className="font-medium text-blue-600">{overview.productivity.activeTasks}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Completed This Month</span>
                <span className="font-medium text-green-600">{overview.productivity.completedTasksThisMonth}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-600">Total Hours This Month</span>
                <span className="font-medium text-purple-600">{overview.productivity.totalHoursThisMonth}h</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </FirmAdminLayout>
  );
};
